<template>
  <teleport to="#headerPageContent">
    <router-link
      #="{ navigate }"
      :to="{
        name: 'Products/Car/Visualise',
        params: {
          carId: $route.params.carId
        }
      }"
      custom
    >
      <HeaderBarItem @click="navigate">
        <HeaderIconButton>
          <template #icon>
            <MagnifierCircleSvg />
          </template>

          <template #label>
            Visualise component build
          </template>
        </HeaderIconButton>
      </HeaderBarItem>
    </router-link>
    <HeaderBarItem>
      <ProductsSwitchViewButton v-model="viewMode" />
    </HeaderBarItem>
  </teleport>

  <div :class="$style.page">
    <BaseContainer>
      <BaseLoader
        :is-active="$apollo.queries.car.loading"
        is-block
      >
        <template v-if="!$apollo.queries.car.loading">
          <ProductsCarHeader
            :car-data="car"
            :volumes-data="volumes ? volumes : []"
            :car-sop="carSop"
            :car-eop="carEop"
            :car-names="carNames"
          />

          <div :class="$style.illustration">
            <ProductsCarImages
              :class="$style.images"
              :front-image="car.front_image"
              :side-image="car.side_image"
              :back-image="car.back_image"
            />
            <div :class="$style.chartWrapper">
              <VolumesChart
                v-if="chartData"
                :class="$style.chart"
                :aspect-ratio="1"
                :datasets="chartData.datasets"
                :labels="chartData.labels"
              />
            </div>
          </div>
        </template>
      </BaseLoader>
    </BaseContainer>
    <div :class="$style.devices">
      <div
        v-if="devicesGrouped['HL']?.length"
        :class="$style.devicesSection"
      >
        <BaseContainer>
          <div :class="$style.devicesSectionHeader">
            <h3 :class="$style.devicesSectionHeading">
              Headlamps on this vehicle
            </h3>
            <p :class="$style.devicesSectionSubheading">
              There are {{ devicesGrouped['HL'].length }} headlamps used on this vehicle.
            </p>
          </div>
        </BaseContainer>
        <BaseContainer v-if="viewMode === 'list'">
          <BaseTable
            :data="devicesGrouped['HL']"
            is-block
          >
            <DevicesTableColumns has-supplier />
          </BaseTable>
        </BaseContainer>
        <div v-else-if="viewMode === 'grid'">
          <ProductsCardsGrid>
            <ProductsDeviceCard
              v-for="device in devicesGrouped['HL']"
              :key="device.id"
              :device-id="device.id"
              :type="device.type"
              :supplier-name="device.supplier.name"
              :supplier-logo="device.supplier.logo"
              :brand-name="device.lighting_configurations?.[0]?.car.brand.name"
              :brand-logo="device.lighting_configurations?.[0]?.car.brand.logo"
              :supplier-region="device.supplier.region"
              :supplier-country="device.supplier.country"
              :front-image="device.front_image"
              :car-image="device.car_image"
              :min-sop="device.min_sop"
              :max-eop="device.max_eop"
              :cars-number="device.lighting_configurations.length"
              :volume="device.volume"
            />
          </ProductsCardsGrid>
        </div>
      </div>

      <div
        v-if="devicesGrouped['RL']?.length"
        :class="$style.devicesSection"
      >
        <BaseContainer>
          <div :class="$style.devicesSectionHeader">
            <h3 :class="$style.devicesSectionHeading">
              Rear lamps on this vehicle
            </h3>
            <p :class="$style.devicesSectionSubheading">
              There are {{ devicesGrouped['RL'].length }} rear lamps used on this vehicle.
            </p>
          </div>
        </BaseContainer>
        <BaseContainer v-if="viewMode === 'list'">
          <BaseTable
            :data="devicesGrouped['RL']"
            is-block
          >
            <DevicesTableColumns has-supplier />
          </BaseTable>
        </BaseContainer>
        <div v-else-if="viewMode === 'grid'">
          <ProductsCardsGrid>
            <ProductsDeviceCard
              v-for="device in devicesGrouped['RL']"
              :key="device.id"
              :device-id="device.id"
              :type="device.type"
              :supplier-name="device.supplier.name"
              :supplier-logo="device.supplier.logo"
              :brand-name="device.lighting_configurations?.[0]?.car.brand.name"
              :brand-logo="device.lighting_configurations?.[0]?.car.brand.logo"
              :supplier-region="device.supplier.region"
              :supplier-country="device.supplier.country"
              :front-image="device.front_image"
              :car-image="device.car_image"
              :min-sop="device.min_sop"
              :max-eop="device.max_eop"
              :cars-number="device.lighting_configurations.length"
              :volume="device.volume"
            />
          </ProductsCardsGrid>
        </div>
      </div>

      <div
        v-if="devicesGrouped['other']?.length"
        :class="$style.devicesSection"
      >
        <BaseContainer>
          <div :class="$style.devicesSectionHeader">
            <h3 :class="$style.devicesSectionHeading">
              Other lighting devices on this vehicle
            </h3>
            <p :class="$style.devicesSectionSubheading">
              There are {{ devicesGrouped['other'].length }} additional devices used on this vehicle.
            </p>
          </div>
        </BaseContainer>
        <BaseContainer v-if="viewMode === 'list'">
          <BaseTable
            :data="devicesGrouped['other']"
            is-block
          >
            <DevicesTableColumns
              has-supplier
              has-type
            />
          </BaseTable>
        </BaseContainer>
        <div
          v-else-if="viewMode === 'grid'"
        >
          <ProductsCardsGrid>
            <ProductsDeviceCard
              v-for="device in devicesGrouped['other']"
              :key="device.id"
              :device-id="device.id"
              :type="device.type"
              :supplier-name="device.supplier.name"
              :supplier-logo="device.supplier.logo"
              :brand-name="device.lighting_configurations?.[0]?.car.brand.name"
              :brand-logo="device.lighting_configurations?.[0]?.car.brand.logo"
              :supplier-region="device.supplier.region"
              :supplier-country="device.supplier.country"
              :front-image="device.front_image"
              :car-image="device.car_image"
              :min-sop="device.min_sop"
              :max-eop="device.max_eop"
              :cars-number="device.lighting_configurations.length"
              :volume="device.volume"
              show-type
            />
          </ProductsCardsGrid>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { mapState } from 'vuex';
import gql from 'graphql-tag';
import { maxBy, minBy, sumBy } from 'lodash';
import BaseContainer from '@/components/base/Container';
import BaseLoader from '@/components/base/Loader';
import BaseTable from '@/components/base/Table';
import VolumesChart from '@/components/VolumesChart';
import ProductsCarHeader from '@/components/ProductsCarHeader';
import ProductsCarImages from '@/components/ProductsCarImages';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderIconButton from '@/components/HeaderIconButton';
import ProductsCardsGrid from '@/components/ProductsCardsGrid';
import ProductsDeviceCard from '@/components/ProductsDeviceCard';
import ProductsSwitchViewButton from '@/components/ProductsSwitchViewButton';
import DevicesTableColumns from '@/components/DevicesTableColumns';
import MagnifierCircleSvg from '@/assets/images/icons/magnifier-circle.svg?inline';
import { groupByYear, convertGroupedByYearToChartData, styleYearSegments } from '@/charts';

export default {
  components: {
    BaseContainer,
    BaseLoader,
    BaseTable,
    VolumesChart,
    ProductsCarHeader,
    ProductsCarImages,
    HeaderBarItem,
    HeaderIconButton,
    ProductsCardsGrid,
    ProductsDeviceCard,
    ProductsSwitchViewButton,
    DevicesTableColumns,
    MagnifierCircleSvg,
  },

  apollo: {
    car: {
      query: gql`
        query ($carId: Int!) {
          cars(where: {id: {_eq: $carId}}) {
            id
            model
            commercial_name
            project_code
            generation
            phase
            body
            power_train
            sop
            eop
            front_image
            side_image
            back_image
            brand {
              id
              logo
              name
            }
            country {
              region
              code
            }
          }
        }
      `,
      variables() {
        return {
          carId: this.$route.params.carId,
        };
      },
      update(data) {
        this.loadVolume(data.cars[0], this.user);
        return data.cars[0];
      },
      skip() {
        return this.$route.params.carId == null;
      },
    },
    sameModelCars: {
      query: gql`
        query ($brandId: Int!, $model: String!) {
          sameModelCars: cars(where: { brand_id: { _eq: $brandId }, model: { _eq: $model }}) {
            commercial_name
            production_factory {
              region
            }
          }
        }
      `,
      variables() {
        return {
          brandId: this.car.brand.id,
          model: this.car.model,
        };
      },
      skip() {
        return this.car.id == null;
      },
    },
  },
  data() {
    return {
      car: {},
      sameModelCars: null,
      devices: [],
      volumes: null,
      viewMode: 'grid',
      carSop: 0,
      carEop: 0,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('app', ['updateReferenceId']),
    carNames() {
      if (this.sameModelCars == null) return null;

      const res = this.sameModelCars.reduce((acc, car) => {
        if (acc[car.commercial_name] == null) acc[car.commercial_name] = new Set();

        acc[car.commercial_name].add(car.production_factory.region);

        return acc;
      }, {});

      Object.keys(res).forEach((key) => {
        res[key] = Array.from(res[key]);
      });

      return res;
    },
    currentCarVolumes() {
      if (this.volumes == null) return null;

      return this.volumes.filter((item) => item.car.id === this.car.id);
    },
    chartData() {
      if (this.volumes == null) return null;

      const groupedByYears = groupByYear(this.volumes, 'car.id');
      const currentCarGroupedByYears = groupByYear(this.currentCarVolumes, 'car.id');

      const yearsSet = new Set();
      this.volumes.forEach((item) => yearsSet.add(item.year));

      return {
        labels: Array.from(yearsSet).sort(),
        datasets: [
          {
            label: `${this.car.brand.name}  ${this.car.model}`,
            borderColor: '#FFA829',
            backgroundColor: '#FFA829',
            pointBackgroundColor: '#fff',
            segment: {
              borderDash: styleYearSegments,
            },
            data: convertGroupedByYearToChartData(groupedByYears),
          },
          {
            label: 'This car',
            borderColor: '#B4E43B',
            backgroundColor: '#B4E43B',
            pointBackgroundColor: '#fff',
            segment: {
              borderDash: styleYearSegments,
            },
            data: convertGroupedByYearToChartData(currentCarGroupedByYears),
          },
        ],
      };
    },
    devicesGrouped() {
      return this.devices.reduce((acc, device) => {
        let group;
        if (['HL', 'RL'].includes(device.type)) {
          group = device.type;
        } else {
          group = 'other';
        }

        if (acc[group] == null) acc[group] = [];

        acc[group].push(device);

        return acc;
      }, {});
    },
  },
  async created() {
    const { data } = await this.$apollo.query({
      query: gql`
        query ($carId: Int!) {
          lightingConfigurations: lighting_configurations(
            where: {
              car_id: {_eq: $carId}
            },
            order_by: {id: asc}
          ) {

            lighting_device {
              id
              type
              front_image
              part_number
              aftermarket_price
              parts_per_car
              complexity
              supplier {
                name
                logo
                region
                country
              }
              lighting_configurations {
                car {
                  brand {
                    name
                    logo
                  }
                  back_image
                  front_image
                  sop
                  eop
                  volumes_infos {
                   sop: sop_${this.user.company.uuid.replace(/-/g, '')}
                   eop: eop_${this.user.company.uuid.replace(/-/g, '')}
                   has_volumes: has_volumes_${this.user.company.uuid.replace(/-/g, '')}
                  }
                }
                take_rates_aggregate {
                  aggregate {
                    sum {
                      units
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        carId: this.$route.params.carId,
      },
    });
    this.devices = data.lightingConfigurations.map((lightingConfiguration) => {
      const device = lightingConfiguration.lighting_device;
      let lightingConfigurations = [];
      lightingConfigurations = device.lighting_configurations.map((lc) => {
        let result = lc.car;
        result = {
          ...result,
          volumes_infos: result.volumes_infos[0],
        };
        return {
          ...lc,
          car: result,
        };
      });
      const minSopConfiguration = minBy(lightingConfigurations, 'car.volumes_infos.sop');
      const maxEopConfiguration = maxBy(lightingConfigurations, 'car.volumes_infos.eop');
      const deviceSide = device.type?.includes('HL') ? 'front' : 'back';
      const carImage = deviceSide === 'front'
        ? device.lighting_configurations?.[0]?.car.front_image
        : device.lighting_configurations?.[0]?.car.back_image;
      const volume = sumBy(device.lighting_configurations, 'take_rates_aggregate.aggregate.sum.units');

      return {
        ...device,
        min_sop: minSopConfiguration?.car.volumes_infos.sop,
        max_eop: maxEopConfiguration?.car.volumes_infos.eop,
        car_image: carImage,
        volume,
      };
    });
  },
  methods: {
    async loadVolume(car, user) {
      const { data } = await this.$apollo.query({
        query: gql`query {
            volumes: volumes_${user.company.uuid.replace(/-/g, '')}(where: {
              _and: { 
                units: { _is_null: false, _neq: 0 }, 
                brand_id: { _eq: ${car.brand.id} }, 
                model: { _eq: "${car.model}" },
                update_ref_id: { _eq: "${this.updateReferenceId}"},
              },
            }) {
              year
              units
              sop
              eop
              car {
                id
                model
              }
            }

            volumes_infos : cars_sop_eop(where: {car_id: {_eq: ${car.id}}}){
              sop: sop_${user.company.uuid.replace(/-/g, '')}
              eop: eop_${user.company.uuid.replace(/-/g, '')}
            }
          }
        `,
      });

      this.volumes = data.volumes;
      this.carSop = format(new Date(data.volumes.length > 0 ? data.volumes_infos[0].sop : 0), 'MMMM y');
      this.carEop = format(new Date(data.volumes.length > 0 ? data.volumes_infos[0].eop : 0), 'MMMM y');
    },
  },
};
</script>

<style lang="scss" module>
.page {
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
}

.illustration {
  display: flex;
  padding: 0 15px;
  margin-top: 20px;
}

.images {
  flex-grow: 1;
}

.chartWrapper {
  padding: 15px 20px;
  margin-left: 8px;
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1),
    0 3px 20px rgba(0, 0, 0, 0.1);
}

.chart {
  width: 350px;
}

.devices {
  margin-top: 15px;
}

.devicesSection {
  margin-top: 35px;

  &:not(:first-child) {

    .devicesSectionHeader {
      position: relative;
      padding-top: 35px;

      &::before {
        position: absolute;
        top: 0;
        right: 15px;
        left: 15px;
        display: block;
        width: 100%;
        height: 1px;
        content: '';
        background-color: rgba($disabled, 0.4);
      }
    }
  }
}

.devicesSectionHeader {
  padding: 0 15px;
}

.devicesSectionHeading {
  padding: 0;
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  color: $title;
}

.devicesSectionSubheading {
  margin: 0;
  margin-top: 4px;
  font-size: 16px;
}
</style>
